/**
 * Download a file using direct browser navigation. Works through proxies and iframes.
 * Uses a hidden anchor tag click as the primary method (most reliable across all environments).
 * Returns a promise that resolves when download initiates.
 */
export async function downloadFile(path) {
  const token = sessionStorage.getItem("auth_token");
  if (!token) throw new Error("Not authenticated");

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  
  // Build the download URL with token
  const downloadPath = path
    .replace(`${backendUrl}/api/export/`, `${backendUrl}/api/download/`)
    .replace("/api/export/", "/api/download/");
  
  const separator = downloadPath.includes("?") ? "&" : "?";
  const url = `${downloadPath}${separator}token=${encodeURIComponent(token)}`;

  // Method 1: Hidden anchor click (most reliable - doesn't trigger popup blockers or X-Frame-Options)
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); }, 100);
}

/**
 * Direct data export with query params (for reports without saved ID)
 */
export async function downloadDirectExcel(name, startDate, endDate) {
  const token = sessionStorage.getItem("auth_token");
  if (!token) throw new Error("Not authenticated");

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const params = new URLSearchParams({ token });
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  if (name) params.set("name", name);
  
  const url = `${backendUrl}/api/download/excel/data?${params.toString()}`;
  
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); }, 100);
}

/**
 * Generic export helper with loading state and error handling.
 * Wraps downloadFile with toast feedback.
 * @param {string} path - export path
 * @param {function} setExporting - state setter for loading indicator
 * @param {object} toast - sonner toast object
 */
export async function exportWithFeedback(path, setExporting, toast) {
  if (setExporting) setExporting(true);
  try {
    await downloadFile(path);
    if (toast) toast.success("Export started — check your downloads");
  } catch (err) {
    const msg = err?.message || "Export failed";
    if (toast) toast.error(msg);
    throw err;
  } finally {
    // Small delay to show loading state
    setTimeout(() => { if (setExporting) setExporting(false); }, 1500);
  }
}

